import {
  MultiFilterOperator,
  SearchZapsOrder,
  SearchZapsStatusWithAll,
} from "@zapier/bff";
import { ZAP_PAGE_SIZE } from "@zapier/utils";
import { RouterOutputs, trpc } from "../trpc";
import { keepPreviousData } from "@tanstack/react-query";
import { useLimitOffsetFromPage } from "../../hooks/useLimitOffsetFromPage";

export type SearchZap = RouterOutputs["zap"]["searchZaps"]["results"][number];
export type SearchZaps = RouterOutputs["zap"]["searchZaps"];
interface Input {
  appId?: string;
  apps?: string;
  appsOperator?: MultiFilterOperator;
  connection?: number;
  connections?: string;
  lastRun?: string;
  page?: number | null;
  pageSize?: number;
  search?: string;
  owner?: string;
  owners?: string;
  ownersOperator?: MultiFilterOperator;
  order?: SearchZapsOrder;
  status?: SearchZapsStatusWithAll;
  enabled?: boolean;
  limit?: number;
}

export function useQuerySearchZaps({
  appId = "",
  apps,
  appsOperator,
  connection,
  connections,
  lastRun,
  page = 1,
  pageSize = ZAP_PAGE_SIZE,
  search = "",
  owner = "",
  owners,
  ownersOperator,
  order = "-updated_at",
  status = "all",
  enabled = true,
  limit,
}: Input) {
  const { limit: limitFromPage, offset } = useLimitOffsetFromPage(
    page ?? 1,
    pageSize,
  );

  const useMultiOwners = !!owners;
  const useMultiApps = !!apps;
  const connectionsArgument =
    connections ?? (connection ? String(connection) : undefined);

  const zapsResponse = trpc.zap.searchZaps.useQuery(
    {
      limit: limit ?? limitFromPage,
      offset,
      order,
      ...(useMultiApps
        ? { apps, ...(appsOperator ? { appsOperator } : {}) }
        : appId
          ? { apps: appId }
          : {}),
      ...(connectionsArgument ? { connections: connectionsArgument } : {}),
      ...(lastRun
        ? {
            lastRun: lastRun as
              | "within_7"
              | "within_30"
              | "within_60"
              | "over_60",
          }
        : {}),
      ...(status && status !== "all" ? { status } : {}),
      ...(search.length > 0 ? { search } : {}),
      ...(useMultiOwners
        ? {
            owners,
            ...(ownersOperator ? { ownersOperator } : {}),
          }
        : owner
          ? { owner }
          : {}),
    },
    {
      enabled,
      placeholderData: keepPreviousData,
      trpc: {
        context: {
          // Skip batching to avoid bundling this critical procedure with slower requests
          skipBatching: true,
        },
      },
    }, // keeps the old results while new results are being fetched after filter changes
  );

  return {
    response: zapsResponse.data,
    isLoading: zapsResponse.isLoading,
    isFetching: zapsResponse.isFetching,
  };
}
